import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import StarRating from './StarRating';
import EmojiPicker from './EmojiPicker';
import { submitReview, getMyReview } from '../lib/api/reviewsApi';

// Formulaire d'avis affiché après un scan (dans le verdict). Désactivé en démo.
// Si l'utilisateur a déjà laissé un avis (même compte), il est préchargé et modifiable.
function ReviewForm({ steamId, isDemo }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState('');
  const [name, setName] = useState('');
  const [existing, setExisting] = useState(false);
  const [done, setDone] = useState(null); // null | 'created' | 'updated'
  const [error, setError] = useState(null);
  const textRef = useRef(null);

  // Insère un emoji à la position du curseur dans le commentaire.
  const insertEmoji = (emoji) => {
    const el = textRef.current;
    if (!el) {
      setComment((c) => c + emoji);
      return;
    }
    const start = el.selectionStart ?? comment.length;
    const end = el.selectionEnd ?? comment.length;
    setComment(comment.slice(0, start) + emoji + comment.slice(end));
    requestAnimationFrame(() => {
      el.focus();
      const pos = start + emoji.length;
      el.setSelectionRange(pos, pos);
    });
  };

  // Précharge l'avis existant de ce compte (pour l'éditer).
  useEffect(() => {
    if (!steamId || isDemo) return undefined;
    let alive = true;
    getMyReview(steamId)
      .then(({ data }) => {
        if (!alive || !data) return;
        setExisting(true);
        setRating(data.rating || 0);
        setComment(data.comment || '');
        setName(data.authorName || '');
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [steamId, isDemo]);

  const submit = async (e) => {
    e.preventDefault();
    if (isDemo) return;
    if (rating < 1) {
      setError(t('reviews.pick_rating'));
      return;
    }
    try {
      const { data } = await submitReview({
        rating,
        comment: comment.trim() || null,
        authorName: name.trim() || null,
        steamId: steamId || null,
      });
      setDone(data?.updated ? 'updated' : 'created');
    } catch {
      setError(t('reviews.error'));
    }
  };

  if (done) {
    return (
      <section className="reviewform reviewform--done">
        <StarRating value={rating} readOnly size="md" />
        <p>{done === 'updated' ? t('reviews.updated') : t('reviews.thanks')}</p>
      </section>
    );
  }

  return (
    <section className="reviewform">
      {!open ? (
        <div className="reviewform__invite">
          <div className="reviewform__invite-stars" aria-hidden="true">★★★★★</div>
          <h3 className="reviewform__invite-title">
            {existing ? t('reviews.invite_title_edit') : t('reviews.invite_title')}
          </h3>
          <p className="reviewform__invite-sub">{t('reviews.invite_sub')}</p>
          <button type="button" className="btn btn--amber reviewform__invite-btn" onClick={() => setOpen(true)}>
            ⭐ {existing ? t('reviews.edit_cta') : t('reviews.leave_cta')}
          </button>
        </div>
      ) : (
        <form className="reviewform__form" onSubmit={submit}>
          <h3 className="reviewform__title">
            {existing ? t('reviews.form_title_edit') : t('reviews.form_title')}
          </h3>
          <StarRating value={rating} onChange={setRating} size="lg" />
          <input
            className="reviewform__name"
            type="text"
            placeholder={t('reviews.name_ph')}
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={80}
          />
          <textarea
            ref={textRef}
            className="reviewform__text"
            rows="4"
            placeholder={t('reviews.comment_ph')}
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            maxLength={1000}
          />
          <div className="reviewform__tools">
            <EmojiPicker onSelect={insertEmoji} />
            <span className="reviewform__count">{comment.length}/1000</span>
          </div>
          {error && <p className="reviewform__error">{error}</p>}
          {isDemo && <p className="reviewform__demo">{t('reviews.demo_disabled')}</p>}
          <div className="reviewform__actions">
            <button type="button" className="btn btn--ghost" onClick={() => setOpen(false)}>
              {t('reviews.cancel')}
            </button>
            <button type="submit" className="btn btn--amber" disabled={isDemo}>
              {existing ? t('reviews.update') : t('reviews.send')}
            </button>
          </div>
        </form>
      )}
    </section>
  );
}

export default ReviewForm;
